import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  keyframes,
} from '@mui/material';
import {
  HourglassEmpty,
  CheckCircleOutline,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

// Animação de rotação para o ícone
const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Animação de pulsação para o avatar
const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
`;

export interface PendingOperationData {
  operation_code?: string;
  status?: string;
  dados?: any;
  campos_faltantes?: string[];
  proximo_campo?: string;
  resumo?: string;
  url?: string;
}

export interface PendingOperationViewProps {
  pendingData: PendingOperationData;
}

const getOperationTitle = (t: any, operationCode?: string): string => {
  switch (operationCode) {
    case 'cadastro_animal':
      return t('farm:pendingOperation.operations.animalRegistration');
    case 'cadastro_lote':
      return t('farm:pendingOperation.operations.lotRegistration');
    case 'registrar_vacinacao':
      return t('farm:pendingOperation.operations.vaccinationRecord');
    case 'registrar_medicacao':
      return t('farm:pendingOperation.operations.medicationRecord');
    case 'registrar_nutricao':
      return t('farm:pendingOperation.operations.nutritionRecord');
    case 'transferir_animal':
      return t('farm:pendingOperation.operations.animalTransfer');
    default:
      return t('farm:pendingOperation.title');
  }
};

const getOperationIcon = (operationCode?: string) => {
  switch (operationCode) {
    case 'cadastro_animal':
      return (
        <Box
          component="img"
          src="/vaca.svg"
          alt="Animal"
          sx={{
            width: 24,
            height: 24,
            color: DESIGN_TOKENS.colors.primary[700]
          }}
        />
      );
    default:
      return <HourglassEmpty sx={{ color: DESIGN_TOKENS.colors.primary[600] }} />;
  }
};

const getFieldLabel = (t: any, field: string): string => {
  const fieldLabels: { [key: string]: string } = {
    name: t('farm:animal.fields.name'),
    breed_code: t('farm:animal.fields.breed_code'),
    gender: t('farm:animal.fields.gender'),
    birth_date: t('farm:animal.fields.birth_date'),
    weight: t('farm:animal.fields.weight'),
    paddock: t('farm:animal.fields.paddock'),
    vaccine_name: t('farm:pendingOperation.fields.vaccine_name'),
    dose: t('farm:pendingOperation.fields.dose'),
    application_date: t('farm:pendingOperation.fields.application_date'),
    medication_name: t('farm:pendingOperation.fields.medication_name'),
    quantity: t('farm:pendingOperation.fields.quantity'),
    feed_type: t('farm:pendingOperation.fields.feed_type'),
    amount: t('farm:pendingOperation.fields.amount'),
    destination_paddock: t('farm:pendingOperation.fields.destination_paddock'),
  };
  
  return fieldLabels[field] || field;
};

export const PendingOperationView: React.FC<PendingOperationViewProps> = ({
  pendingData,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  
  const {
    operation_code,
    status,
    campos_faltantes = [],
    proximo_campo,
    resumo,
  } = pendingData;

  const operationTitle = getOperationTitle(t, operation_code);
  const totalFields = campos_faltantes.length;
  const progress = 0; // Como está incompleto, progresso é 0

  return (
    <Box sx={{ 
      width: '100%',
      height: 'fit-content',
      background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.background.default} 100%)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      p: 4,
      position: 'relative',
      borderRadius: 2,
      maxWidth: 600,
      mx: 'auto',
      my: 2
    }}>
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${DESIGN_TOKENS.colors.primary[100]}40 0%, transparent 70%)`,
          animation: `${pulseAnimation} 4s ease-in-out infinite`,
        }}
      />
      
      <Box
         sx={{
           position: 'absolute',
           bottom: -30,
           left: -30,
           width: 150,
           height: 150,
           borderRadius: '50%',
           background: `radial-gradient(circle, ${DESIGN_TOKENS.colors.primary[100]}30 0%, transparent 70%)`,
           animation: `${pulseAnimation} 3s ease-in-out infinite reverse`,
         }}
      />
      
      {/* Main content */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        <Avatar
           sx={{
             width: 120,
             height: 120,
             bgcolor: 'white',
             mb: 4,
             boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.primary[100]}60, 0 0 0 1px ${DESIGN_TOKENS.colors.primary[100]}`,
             border: `3px solid ${DESIGN_TOKENS.colors.primary[100]}`,
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
           }}
         >
           <HourglassEmpty 
             sx={{ 
               fontSize: '3.5rem', 
               color: DESIGN_TOKENS.colors.primary[700],
               animation: `${rotateAnimation} 3s linear infinite`,
             }} 
           />
         </Avatar>
         
         <Typography
           variant="h3"
           sx={{
             fontWeight: 700,
             color: DESIGN_TOKENS.colors.text.primary,
             mb: 3,
             letterSpacing: '-0.02em'
           }}
         >
           {operationTitle}
         </Typography>
         
         <Card
           sx={{
             maxWidth: 400,
             bgcolor: 'rgba(255, 255, 255, 0.8)',
             backdropFilter: 'blur(10px)',
             border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
             borderRadius: 3,
             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
             mb: 4
           }}
         >
           <CardContent sx={{ p: 3 }}>
             <Typography
               variant="body2"
               sx={{
                 color: DESIGN_TOKENS.colors.text.secondary,
                 mb: 2,
                 fontWeight: 500
               }}
             >
               {t('farm:pendingOperation.continueInChat')}
             </Typography>
             <Typography
               variant="body2"
               sx={{
                 color: DESIGN_TOKENS.colors.text.primary,
                 lineHeight: 1.5
               }}
             >
               {t('farm:pendingOperation.provideInformation')}
             </Typography>
           </CardContent>
         </Card>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
           <Box
             sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               bgcolor: DESIGN_TOKENS.colors.primary[600],
               animation: `${pulseAnimation} 1s ease-in-out infinite`,
             }}
           />
           <Typography
             variant="body2"
             sx={{
               color: DESIGN_TOKENS.colors.primary[700],
               fontWeight: 500,
               textTransform: 'uppercase',
               letterSpacing: '0.1em',
               fontSize: '0.85rem'
             }}
           >
             {t('farm:pendingOperation.processing')}
           </Typography>
           <Box
             sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               bgcolor: DESIGN_TOKENS.colors.primary[600],
               animation: `${pulseAnimation} 1s ease-in-out infinite 0.5s`,
             }}
           />
         </Box>
      </Box>
    </Box>
  );
};

export default PendingOperationView;